import { Platform } from "react-native";

const GTM_CONTAINER_ID = 'GTM-KHFSLNT';
const PURCHASE_STORAGE_KEY = 'gtm_pending_purchase';
const PURCHASE_SENT_KEY = 'gtm_last_purchase_signature';

const isWeb = () => Platform.OS === 'web' && typeof window !== 'undefined';

const ensureDataLayer = () => {
  if (!isWeb()) return null;
  window.dataLayer = window.dataLayer || [];
  return window.dataLayer;
};

export const loadGoogleTagManager = () => {
  if (!isWeb()) return false;

  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1' || window.location.port === '8443') {
    //console.log('🔧 Desarrollo local detectado - GTM no cargado');
    return false;
  }

  if (window.__gtmLoaded) return true;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    'gtm.start': new Date().getTime(),
    event: 'gtm.js',
  });

  const existingScript = document.querySelector(`script[src*="googletagmanager.com/gtm.js?id=${GTM_CONTAINER_ID}"]`);
  if (!existingScript) {
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_CONTAINER_ID}`;
    document.head.appendChild(script);
  }

  const existingNoscript = document.getElementById('gtm-noscript-container');
  if (!existingNoscript && document.body) {
    const noscript = document.createElement('noscript');
    noscript.id = 'gtm-noscript-container';
    const iframe = document.createElement('iframe');
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${GTM_CONTAINER_ID}`;
    iframe.height = '0';
    iframe.width = '0';
    iframe.style.display = 'none';
    iframe.style.visibility = 'hidden';
    noscript.appendChild(iframe);
    document.body.insertBefore(noscript, document.body.firstChild);
  }

  window.__gtmLoaded = true;
  //console.log('✅ Google Tag Manager cargado en producción');
  return true;
};

export const pushDataLayerEvent = (eventName, payload = {}) => {
  const dataLayer = ensureDataLayer();
  if (!dataLayer) return;

  const eventPayload = {
    event: eventName,
    ...payload,
  };

  dataLayer.push(eventPayload);
 // console.log(`📊 dataLayer.push: ${eventName}`, eventPayload);
};

export const buildPagePathFromRoute = (route) => {
  if (!route) return '/';

  const { name, params } = route;

  switch (name) {
    case '':
      return '/';
    case 'Productos':
      return '/productos';
    case 'DescripcionProducto':
      return `/producto/${params?.id ?? ''}`.replace(/\/$/, '');
    case 'PromosBancos':
      return '/PromosBanco';
    case 'Historia':
      return '/Historia';
    case 'Manifiesto':
      return '/manifiesto';
    case 'PoliticasDevolucion':
      return '/politicas-de-devolucion';
    case 'TerminosYCondiciones':
      return '/terminos-y-condiciones';
    case 'ArrepentimientoDeCompra':
      return '/arrepentimiento-de-compra';
    case 'validar':
      return '/validar';
    case 'CheckoutFinalizarCompra':
      return '/checkout';
    case 'PromocionDetalle':
      return `/promocion/${params?.id ?? ''}`.replace(/\/$/, '');
    case 'Perfil':
      return '/perfil';
    case 'PagoOK':
    case 'PagoOKWeb':
      return '/pagoOK';
    case 'PagoFail':
    case 'PagoFailWeb':
      return '/pagoFail';
    default:
      return '/404';
  }
};

export const trackPageView = (route) => {
  if (!isWeb()) return;

  const page_path = buildPagePathFromRoute(route);
  const page_location = `${window.location.origin}${page_path}`;
  const page_title = route?.name || document.title || 'Sin título';

  pushDataLayerEvent('page_view', {
    page_path,
    page_location,
    page_title,
    screen_name: route?.name || '',
  });
};

export const trackAddToCart = ({ plu, quantity, product_name } = {}) => {
  pushDataLayerEvent('add_to_cart', {
    content_ids: plu ? [String(plu)] : [],
    content_type: 'product',
    quantity: Number(quantity || 1),
    product_name: product_name || '',
  });
};

export const trackCreateAccountClick = ({ email } = {}) => {
  pushDataLayerEvent('create_account_click', {
    email: email || '',
  });
};

export const trackCompleteRegistration = ({ email } = {}) => {
  pushDataLayerEvent('complete_registration', {
    status: 'verified',
    email: email || '',
  });
};

export const savePendingPurchase = (payload = {}) => {
  if (!isWeb()) return;

  const normalized = {
    value: Number(payload.value || 0),
    currency: payload.currency || 'ARS',
    content_ids: Array.isArray(payload.content_ids) ? payload.content_ids.map(String) : [],
    contents: Array.isArray(payload.contents) ? payload.contents : [],
    num_items: Number(payload.num_items || 0),
    payment_method: payload.payment_method || '',
    event_id: payload.event_id || `${Date.now()}`,
  };

  window.localStorage.setItem(PURCHASE_STORAGE_KEY, JSON.stringify(normalized));
};

export const clearPendingPurchase = () => {
  if (!isWeb()) return;
  window.localStorage.removeItem(PURCHASE_STORAGE_KEY);
};

export const getPendingPurchase = () => {
  if (!isWeb()) return null;
  const raw = window.localStorage.getItem(PURCHASE_STORAGE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch (error) {
    return null;
  }
};

export const trackPurchase = (purchasePayload = null) => {
  if (!isWeb()) return false;

  const payload = purchasePayload || getPendingPurchase();
  if (!payload) return false;

  const signature = JSON.stringify({
    event_id: payload.event_id || '',
    value: payload.value || 0,
    content_ids: payload.content_ids || [],
  });

  const lastSignature = window.sessionStorage.getItem(PURCHASE_SENT_KEY);
  if (lastSignature === signature) {
    return false;
  }

  pushDataLayerEvent('purchase', {
    value: Number(payload.value || 0),
    currency: payload.currency || 'ARS',
    content_ids: Array.isArray(payload.content_ids) ? payload.content_ids : [],
    contents: Array.isArray(payload.contents) ? payload.contents : [],
    num_items: Number(payload.num_items || 0),
    payment_method: payload.payment_method || '',
    event_id: payload.event_id || '',
  });

  window.sessionStorage.setItem(PURCHASE_SENT_KEY, signature);
  clearPendingPurchase();
  return true;
};
